import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ExcalidrawImperativeAPI,
  AppState,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";
import { getNonDeletedElements } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";
import { getCommonBounds, hashElementsVersion } from "@excalidraw/element";
import { exportToSvg } from "@excalidraw/excalidraw";
import {
  useMinimapPreference,
  type MinimapPreference,
} from "./useMinimapPreference";
import "./Minimap.scss";

const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 150;
const MINIMAP_PADDING = 20;
const MINIMAP_INNER_INSET_PX = 12;
const MINIMAP_INNER_WIDTH = MINIMAP_WIDTH - MINIMAP_INNER_INSET_PX * 2;
const MINIMAP_INNER_HEIGHT = MINIMAP_HEIGHT - MINIMAP_INNER_INSET_PX * 2;
const RENDER_DEBOUNCE_MS = 300;
const AUTO_VISIBLE_ZOOM_THRESHOLD = 1.0;

interface MinimapProps {
  api: ExcalidrawImperativeAPI;
}

function shouldShowMinimap(
  preference: MinimapPreference,
  autoVisible: boolean,
): boolean {
  if (preference === "disabled") {
    return false;
  }
  if (preference === "always") {
    return true;
  }
  return autoVisible;
}

function computeAutoVisible(
  appState: AppState,
  elements: readonly NonDeletedExcalidrawElement[],
): boolean {
  if (elements.length === 0) {
    return false;
  }
  const zoomValue = appState.zoom.value;
  if (zoomValue > AUTO_VISIBLE_ZOOM_THRESHOLD) {
    return true;
  }
  const [minX, minY, maxX, maxY] = getCommonBounds(elements);
  const sceneWidth = maxX - minX;
  const sceneHeight = maxY - minY;
  const visibleWidth = appState.width / zoomValue;
  const visibleHeight = appState.height / zoomValue;
  return sceneWidth > visibleWidth || sceneHeight > visibleHeight;
}

interface SceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
}

function computeSceneBounds(
  elements: readonly NonDeletedExcalidrawElement[],
): SceneBounds | null {
  if (elements.length === 0) {
    return null;
  }
  const [minX, minY, maxX, maxY] = getCommonBounds(elements);
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function computeFitTransform(bounds: SceneBounds) {
  const padded = {
    width: bounds.width + MINIMAP_PADDING * 2,
    height: bounds.height + MINIMAP_PADDING * 2,
  };
  const scale = Math.min(
    MINIMAP_INNER_WIDTH / padded.width,
    MINIMAP_INNER_HEIGHT / padded.height,
  );
  const offsetX =
    (MINIMAP_WIDTH - bounds.width * scale) / 2 - bounds.minX * scale;
  const offsetY =
    (MINIMAP_HEIGHT - bounds.height * scale) / 2 - bounds.minY * scale;
  return { scale, offsetX, offsetY };
}

function computeViewportRect(
  appState: AppState,
  transform: { scale: number; offsetX: number; offsetY: number },
) {
  const { scrollX, scrollY, zoom, width, height } = appState;
  const viewSceneX = -scrollX;
  const viewSceneY = -scrollY;
  const viewSceneW = width / zoom.value;
  const viewSceneH = height / zoom.value;

  return {
    left: viewSceneX * transform.scale + transform.offsetX,
    top: viewSceneY * transform.scale + transform.offsetY,
    width: viewSceneW * transform.scale,
    height: viewSceneH * transform.scale,
  };
}

export const Minimap = ({ api }: MinimapProps) => {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [preference] = useMinimapPreference();
  const [autoVisible, setAutoVisible] = useState<boolean>(() =>
    computeAutoVisible(api.getAppState(), api.getSceneElements()),
  );
  const [viewportRect, setViewportRect] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const boundsRef = useRef<SceneBounds | null>(null);
  const transformRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });
  const renderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);
  const lastElementsHashRef = useRef<number>(0);
  const lastFilesKeyRef = useRef<string>("");
  const renderTokenRef = useRef(0);

  const isVisible = shouldShowMinimap(preference, autoVisible);

  useEffect(() => {
    const unsub = api.onChange(
      (
        elements: readonly ExcalidrawElement[],
        appState: AppState,
        _files: BinaryFiles,
      ) => {
        // upstream's onChange delivers deleted elements too; they must not
        // count towards the scene bounds or the "is there anything" check
        const next = computeAutoVisible(
          appState,
          getNonDeletedElements(elements),
        );
        setAutoVisible((prev) => (prev === next ? prev : next));
      },
    );
    return unsub;
  }, [api]);

  const renderThumbnail = useCallback(async () => {
    const myToken = ++renderTokenRef.current;
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const files = api.getFiles();

    const bounds = computeSceneBounds(elements);
    boundsRef.current = bounds;
    const container = svgContainerRef.current;

    if (!bounds || !container) {
      transformRef.current = { scale: 1, offsetX: 0, offsetY: 0 };
      if (container) {
        container.replaceChildren();
      }
      setViewportRect({
        left: 0,
        top: 0,
        width: MINIMAP_WIDTH,
        height: MINIMAP_HEIGHT,
      });
      return;
    }

    const transform = computeFitTransform(bounds);
    transformRef.current = transform;

    try {
      const svg = await exportToSvg({
        elements,
        appState: {
          ...appState,
          exportBackground: false,
          exportWithDarkMode: appState.theme === "dark",
        },
        files,
        exportPadding: MINIMAP_PADDING,
      });

      if (myToken !== renderTokenRef.current) {
        return;
      }

      const padded = {
        width: bounds.width + MINIMAP_PADDING * 2,
        height: bounds.height + MINIMAP_PADDING * 2,
      };
      const fitScale = Math.min(
        MINIMAP_INNER_WIDTH / padded.width,
        MINIMAP_INNER_HEIGHT / padded.height,
      );
      svg.setAttribute("width", `${padded.width * fitScale}`);
      svg.setAttribute("height", `${padded.height * fitScale}`);
      svg.style.display = "block";
      svg.style.pointerEvents = "none";

      container.replaceChildren(svg);
    } catch {
      // exportToSvg can fail if fonts aren't loaded yet; skip silently
    }

    if (myToken !== renderTokenRef.current) {
      return;
    }
    setViewportRect(computeViewportRect(appState, transform));
  }, [api]);

  const updateViewportOnly = useCallback(() => {
    const appState = api.getAppState();
    if (!boundsRef.current) {
      return;
    }
    setViewportRect(computeViewportRect(appState, transformRef.current));
  }, [api]);

  const scheduleRender = useCallback(() => {
    const elements = api.getSceneElements();
    const files = api.getFiles();
    const elementsHash = hashElementsVersion(elements);
    const filesKey = Object.keys(files).sort().join(",");
    if (
      elementsHash === lastElementsHashRef.current &&
      filesKey === lastFilesKeyRef.current
    ) {
      return;
    }
    lastElementsHashRef.current = elementsHash;
    lastFilesKeyRef.current = filesKey;

    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
    }
    renderTimerRef.current = setTimeout(renderThumbnail, RENDER_DEBOUNCE_MS);
  }, [api, renderThumbnail]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const elements = api.getSceneElements();
    const files = api.getFiles();
    lastElementsHashRef.current = hashElementsVersion(elements);
    lastFilesKeyRef.current = Object.keys(files).sort().join(",");

    renderThumbnail();

    const unsubScroll = api.onScrollChange(updateViewportOnly);

    const unsubChange = api.onChange(
      (
        _elements: readonly ExcalidrawElement[],
        _appState: AppState,
        _files: BinaryFiles,
      ) => {
        scheduleRender();
      },
    );

    return () => {
      unsubScroll();
      unsubChange();
      renderTokenRef.current++;
      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }
    };
  }, [isVisible, api, renderThumbnail, updateViewportOnly, scheduleRender]);

  const navigateToPoint = useCallback(
    (clientX: number, clientY: number) => {
      const wrapper = svgContainerRef.current?.parentElement;
      if (!wrapper || !boundsRef.current) {
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      const mx = clientX - rect.left;
      const my = clientY - rect.top;

      const { scale, offsetX, offsetY } = transformRef.current;
      const sceneX = (mx - offsetX) / scale;
      const sceneY = (my - offsetY) / scale;

      const appState = api.getAppState();
      const viewW = appState.width / appState.zoom.value;
      const viewH = appState.height / appState.zoom.value;

      api.updateScene({
        appState: {
          scrollX: -(sceneX - viewW / 2),
          scrollY: -(sceneY - viewH / 2),
        },
      });
    },
    [api],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      navigateToPoint(e.clientX, e.clientY);
    },
    [navigateToPoint],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) {
        return;
      }
      navigateToPoint(e.clientX, e.clientY);
    },
    [navigateToPoint],
  );

  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="drawola-minimap">
      <div
        className="drawola-minimap__canvas-wrapper"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div ref={svgContainerRef} className="drawola-minimap__canvas" />
        <div
          className="drawola-minimap__viewport"
          style={{
            left: `${viewportRect.left}px`,
            top: `${viewportRect.top}px`,
            width: `${viewportRect.width}px`,
            height: `${viewportRect.height}px`,
          }}
        />
      </div>
    </div>
  );
};
